import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Crown, CalendarDays, Flag, Target, Clock, MapPin, ChevronRight, Calendar, Contact } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { formatNumber } from '../../../utils/numberUtils'
import { Card } from '../../../components/ui'

interface TodayActivity {
  id: number
  title: string
  time_start: string | null
  time_end: string | null
  type_label: string
  status_color: string
  location_name: string | null
  city_name: string | null
}

interface UpcomingActivity {
  id: number
  title: string
  date: string
  time_start: string | null
  status_color: string
  city_name: string | null
}

interface ActiveCampaign {
  id: number
  name: string
  position_label: string
  party_abbreviation: string | null
  electoral_number: string | null
  scope: string
  state_code: string | null
  city_name: string | null
  vote_goal: number | null
  end_date: string | null
}

interface Stats {
  contacts_count: number
  voters_count: number
  leaders_count: number
  month_activities: number
  today_activities: TodayActivity[]
  upcoming_activities: UpcomingActivity[]
}

export default function DashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null)
  const [campaign, setCampaign] = useState<ActiveCampaign | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    Promise.all([
      axios.get(buildApiUrl('/v1/dashboard/stats')),
      axios.get(buildApiUrl('/v1/campaigns')),
    ]).then(([statsRes, campRes]) => {
      setStats(statsRes.data.data)
      const active = campRes.data.data?.find((c: any) => c.is_active_campaign)
      setCampaign(active || null)
    }).catch(() => {}).finally(() => setLoading(false))
  }, [])

  function getDaysRemaining(): string | null {
    if (!campaign?.end_date) return null
    const end = new Date(campaign.end_date + 'T23:59:59')
    const diff = Math.ceil((end.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    if (diff < 0) return 'Encerrada'
    if (diff === 0) return 'Hoje'
    return `${diff} dias`
  }

  function formatWeekday(dateStr: string) {
    const d = new Date(dateStr + 'T12:00:00')
    return ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'][d.getDay()]
  }

  function formatShortDate(dateStr: string) {
    const d = new Date(dateStr + 'T12:00:00')
    return `${d.getDate()}/${d.getMonth() + 1}`
  }

  const daysRemaining = getDaysRemaining()

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-100 rounded animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-[72px] bg-white rounded-2xl border border-gray-200 animate-pulse" />)}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">Visão geral da sua base política</p>
      </div>

      {/* Campanha ativa */}
      {campaign && (
        <div className="bg-gradient-to-r from-brand-950 to-brand-900 rounded-2xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Flag className="w-5 h-5 text-brand-300" />
              </div>
              <div>
                <p className="text-sm font-semibold mb-0.5">{campaign.name}</p>
                <p className="text-white/50 text-sm">
                  {campaign.position_label}
                  {campaign.party_abbreviation && ` · ${campaign.party_abbreviation} ${campaign.electoral_number || ''}`}
                  {campaign.scope === 'STATE' && campaign.state_code && ` · ${campaign.state_code}`}
                  {campaign.scope === 'MUNICIPAL' && campaign.city_name && ` · ${campaign.city_name}`}
                  {campaign.scope === 'NATIONAL' && ' · Brasil'}
                </p>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-6">
              {campaign.vote_goal && (
                <div className="text-right">
                  <p className="text-[11px] text-white/40 uppercase tracking-wider">Meta</p>
                  <p className="text-lg font-bold flex items-center gap-1">
                    <Target className="w-4 h-4 text-brand-300" />
                    {formatNumber(campaign.vote_goal)}
                  </p>
                </div>
              )}
              {daysRemaining && (
                <div className="text-right">
                  <p className="text-[11px] text-white/40 uppercase tracking-wider">Faltam</p>
                  <p className="text-lg font-bold">{daysRemaining}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-4 cursor-pointer hover:shadow-sm transition-shadow" onClick={() => navigate('/contacts')}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-info-500/10 text-info-500">
              <Contact className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-2xl font-semibold text-gray-900">{formatNumber(stats?.contacts_count || 0)}</p>
              <p className="text-xs text-gray-500">Contatos</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300" />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4 cursor-pointer hover:shadow-sm transition-shadow" onClick={() => navigate('/voters')}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-brand-500/10 text-brand-500">
              <Users className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-2xl font-semibold text-gray-900">{formatNumber(stats?.voters_count || 0)}</p>
              <p className="text-xs text-gray-500">Eleitores</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300" />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4 cursor-pointer hover:shadow-sm transition-shadow" onClick={() => navigate('/leaders')}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-success-500/10 text-success-500">
              <Crown className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-2xl font-semibold text-gray-900">{formatNumber(stats?.leaders_count || 0)}</p>
              <p className="text-xs text-gray-500">Lideranças</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300" />
          </div>
        </div>
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Agenda de hoje */}
        <div className="xl:col-span-2">
          <Card>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-gray-800">Agenda de hoje</h2>
              <button onClick={() => navigate('/agenda')} className="text-sm text-brand-500 hover:text-brand-600 font-medium flex items-center gap-1">
                Ver agenda <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {stats?.today_activities && stats.today_activities.length > 0 ? (
              <div className="space-y-3">
                {stats.today_activities.map(a => (
                  <div key={a.id} className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                    <div className="w-1 h-full min-h-[40px] rounded-full flex-shrink-0" style={{ backgroundColor: a.status_color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{a.title}</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-gray-500">
                        {a.time_start && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {a.time_start.substring(0, 5)}{a.time_end ? ` - ${a.time_end.substring(0, 5)}` : ''}
                          </span>
                        )}
                        {(a.location_name || a.city_name) && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {a.location_name || ''}{a.city_name ? `${a.location_name ? ', ' : ''}${a.city_name}` : ''}
                          </span>
                        )}
                        <span className="text-gray-400">{a.type_label}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <Calendar className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Nenhuma atividade para hoje</p>
              </div>
            )}
          </Card>
        </div>

        {/* Próximos dias */}
        <div>
          <Card>
            <h2 className="text-lg font-semibold text-gray-800 mb-5">Hoje</h2>

            {stats?.today_activities && stats.today_activities.length > 0 ? (
              <div className="space-y-3">
                {stats.today_activities.map(a => (
                  <div key={a.id} className="flex items-start gap-3">
                    <div className="flex-1 min-w-0 border-l-2 pl-3" style={{ borderColor: a.status_color }}>
                      <p className="text-sm font-medium text-gray-900 truncate">{a.title}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400">
                        {a.time_start && <span>{a.time_start.substring(0, 5)}{a.time_end ? ` - ${a.time_end.substring(0, 5)}` : ''}</span>}
                        {a.city_name && <span>{a.city_name}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <CalendarDays className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Nenhuma atividade para hoje</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}
